import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {BaseCustomFieldViewComponent} from '../../../base/form';
import {ApiService} from '../../../services/api.service';
import {BehaviorSubject, defer, merge, Subscription} from 'rxjs';
import {
  arr_count,
  arr_filter,
  arr_first,
  arr_map,
  arr_reduce,
  blank,
  data_get,
  data_is_true,
  fallback,
  FieldmagicInputErrors,
  filled,
  generate_id,
  Identifiable,
  is_string,
  observe,
  Omit,
  OptionalValue,
  PartialData,
  spf,
  str_lower,
  to_utc_datetime,
  transform,
  validate_input_data,
  when,
  whenFilled,
} from '../../../shared/utils/common';
import {
  ceil_percentage,
  cost_from_markup,
  divide,
  markup,
  multiply,
  price_from_markup,
  sum,
  to_numeric,
  variance_percentage,
} from '../../../shared/utils/numbers';
import {DIALOG_DATA, DialogService} from '../../../services/dialog.service';
import {RecordService} from '../../../services/record.service';
import {NotificationService} from '../../../services/notification.service';
import {
  FieldmagicDataGridApi,
  FieldmagicDataGridColumnDef,
  FieldmagicDataGridContextMenu,
  FieldmagicDataGridContextMenuParams,
  FieldmagicDataGridOnReadyEvent,
  FieldmagicDataGridOptions,
  FieldmagicDataGridPersistedData,
  FieldmagicDataGridSelectableData,
} from '../../../shared/components/data-grid/data-grid.component';
import {
  FieldmagicDataGridOnRenderedDataClickedEvent,
  FieldmagicDataGridTextRenderingConfig,
} from '../../../shared/components/data-grid/data-grid-cells-renderer.component';
import { TranslateService } from '@ngx-translate/core';
import { FieldmagicRelateInputValue, FieldmagicRelateOption } from '../../../shared/components/forms/input/relate/relate-input.component';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import {
  FieldmagicDataGridDropdownInputConfig,
  FieldmagicDataGridRelateDropdownInputConfig,
} from '../../../shared/components/data-grid/data-grid-inputs.component';
import { DisplayedCategorizedTaskCost, sort_costings } from '../../../admin/project-templates/shared/utils';
import { FieldmagicDropdownOption } from '../../../shared/components/forms/input/dropdown/dropdown.component';
import { JobMetadata } from '../../calendar/full-calendar/classes/metadata/job-metadata';
import { ProjectTaskCalendarEventMetadata } from '../../calendar/full-calendar/classes/metadata/project-task-metadata';
import { RelateData } from '../../calendar/full-calendar/classes/metadata/relate-data';
import { FieldmagicSchedulerDialogComponent, FieldmagicSchedulerDialogData } from '../../../features/new-scheduler/dialogs/scheduler-dialog.component';
import { ItemInterface } from '../../../objects/item';

@Component({
  selector: 'jobs-view-tasks',
  template: `
    <div class="d-flex flex-column d-flex-gap">
      <div fieldmagicGridRow>
        <div fieldmagicGridColumn="12">
          <div class="w-100 d-flex d-flex-gap justify-content-end align-items-center">
            <fieldmagic-primary-button
              variant="link"
              icon="sync-alt"
              label="Refresh"
              (click)="onRefresh()"
              [disabled]="inprogress$ | async"
            >
            </fieldmagic-primary-button>

            <fieldmagic-primary-button
              variant="link"
              [icon]="[
                {
                  icon: 'plus-circle',
                  klass: 'text-success'
                }
              ]"
              label="Add Milestone"
              (click)="onAddMilestone()"
              [disabled]="inprogress$ | async"
            >
            </fieldmagic-primary-button>

            <fieldmagic-primary-button
              variant="link"
              [icon]="[
                {
                  icon: 'plus-circle',
                  klass: 'text-success'
                }
              ]"
              label="Add Task"
              (click)="onAddTask()"
              [disabled]="inprogress$ | async"
            >
            </fieldmagic-primary-button>

            <fieldmagic-primary-button
              icon="clipboard"
              label="Apply Project Template"
              (click)="onApplyTemplate()"
              [disabled]="inprogress$ | async"
            >
            </fieldmagic-primary-button>

            <fieldmagic-primary-button
              icon="users"
              label="Assign Tasks"
              (click)="onAssignTasks()"
              [disabled]="inprogress$ | async"
            >
            </fieldmagic-primary-button>

            <fieldmagic-primary-button
              purpose="save"
              (click)="onSave()"
              [isInProgress]="saving$ | async"
              [disabled]="fetching$ | async"
            >
            </fieldmagic-primary-button>
          </div>
        </div>
      </div>

      <div fieldmagicGridRow>
        <div fieldmagicGridColumn="12">
          <fieldmagic-data-grid
            [columns]="columns"
            [data]="tasks$ | async"
            [options]="options"
            [menus]="menus"
            [fetching]="gridIsBusy$ | async"
            (onReady)="onGridReady($event)"
            storeKey="jobs:cost_centers"
            [dataStateKey]="dataStateKey$ | async"
          >
          </fieldmagic-data-grid>
        </div>
      </div>
    </div>
  `,
  providers: [DialogService],
})
export class ViewJobTasksComponent extends BaseCustomFieldViewComponent implements OnInit, OnDestroy {
  readonly fetching$ = new BehaviorSubject<boolean>(false);
  readonly saving$ = new BehaviorSubject<boolean>(false);
  readonly scheduling$ = new BehaviorSubject<boolean>(false);

  readonly dataStateKey$ = new BehaviorSubject<OptionalValue<string>>(null);

  readonly inprogress$ = merge(
    this.fetching$,
    this.saving$,
  );

  readonly gridIsBusy$ = merge(
    this.fetching$,
    this.scheduling$,
  );

  readonly tasks$ = new BehaviorSubject<DisplayedJobTask[]>([]);

  readonly options: FieldmagicDataGridOptions = {
    enable_ui_grouping: true,
    enable_configuring_columns: true,
  };

  readonly columns: FieldmagicDataGridColumnDef[] = [
    {
      label: 'Milestone',
      grouped: true,
      hidden: true,
      auto_grouped: true,
      pinned: 'left',
      deserializer: (params) => data_get(params.data, 'milestone.name'),
      serializer: (value, params) => ({
        success: true,
        updates: {
          milestone: {
            ... fallback(params.data['milestone'], {
              to: () => ({
                id: generate_id(),
              }),
            }),
            name: value,
          }
        },
      }),
    },
    {
      label: 'Task',
      children: [
        {
          label: 'Task',
          field_name: 'name',
          rendering_config: <FieldmagicDataGridTextRenderingConfig> {
            placeholder: 'Click to manage costs breakdown',
            on_clicked: (event) => this.onManageBreakdown(event),
          },
        },
        {
          label: 'Description',
          field_name: 'description',
          field_type: 'textarea',
        },
      ],
    },
    {
      label: 'Assignee',
      children: [
        {
          label: 'Assignee',
          field_name: 'user',
          field_type: 'relate',
          editable: true,
          enable_grouping: true,
          input_config: <FieldmagicDataGridRelateDropdownInputConfig> {
            remote_config: {
              module_name: 'users',
            },
            placeholder: 'Please select a user',
          },
        },
        {
          label: 'Role',
          field_name: 'role',
          field_type: 'dropdown',
          editable: true,
          enable_grouping: true,
          hide_when_grouped_column_collapsed: true,
          input_config: <FieldmagicDataGridDropdownInputConfig>{
            remote_config: {
              module_name: 'jobs',
              field_name: 'project_roles',
            },
            customizable: true,
            placeholder: 'Please select a role',
          },
        },
        {
          label: 'Department',
          field_name: 'department',
          field_type: 'relate',
          editable: true,
          enable_grouping: true,
          input_config: <FieldmagicDataGridRelateDropdownInputConfig> {
            remote_config: {
              module_name: 'departments',
            },
            placeholder: 'Please select a department',
          },
        },
      ]
    },
    {
      label: 'Status',
      children: [
          {
            label: 'Ready to start?',
            field_name: 'is_ready',
            field_type: 'checkbox',
            serializer: (value: boolean, params) => ({
              success: true,
              updates: {
                'date_ready': when(value, {
                  then: () => to_utc_datetime('now', {
                    as_server_compatible: true,
                  }),
                }),
                'is_ready': value,
              },
            }),
          },
          {
            label: 'Progress',
            field_name: 'progress_percentage',
            field_type: 'progressive_percentage',
            enable_grouping: true,
            serializer: (value: number, params) => {
              value = to_numeric(value);

              const updates = {
                'progress_percentage': value,
                'date_completed': when(value >= 100, {
                  then: () => to_utc_datetime('now', {
                    as_server_compatible: true,
                  }),
                }),
              };

              if (value === 0) {
                updates['date_started'] = null;
              } else if (value > 0 && value < 100 && blank(data_get(params.node.data, 'date_started'))) {
                updates['date_started'] = to_utc_datetime('now', {
                  as_server_compatible: true,
                });
              }

              return {
                success: true,
                updates,
              };
            },
          },
          {
            label: 'Date Ready',
            field_name: 'date_ready',
            field_type: 'datetime',
            hide_when_grouped_column_collapsed: true,
          },
          {
            label: 'Date Started',
            field_name: 'date_started',
            field_type: 'datetime',
            hide_when_grouped_column_collapsed: true,
          },
          {
            label: 'Date Completed',
            field_name: 'date_completed',
            field_type: 'datetime',
            hide_when_grouped_column_collapsed: true,
          },
      ]
    },
    {
      label: 'Estimates',
      children: [
        {
          label: 'Est. Hours',
          field_type: 'numeric',
          field_name: 'estimated_duration',
          hide_when_grouped_column_collapsed: true,
          editable_predicate: (params) => blank(data_get(params.node, 'data.actual_labours')),
          rendering_config: {
            placeholder: (params) => when(filled(data_get(params.node, 'data.actual_labours')), {
              then: () => 'Please click the task name to manage costing for this task',
            }),
          },
          serializer: (value, params) => {
            value = to_numeric(value);

            return ({
              success: true,
              updates: {
                estimated_duration: value,
                estimated_labour_cost: multiply({
                  multiplicant: data_get(params.node, 'data.budget_hourly_rate'),
                  multiplier: value,
                }),
              },
            });
          },
        },
        {
          label: 'Budget Hourly Rate',
          field_type: 'currency',
          field_name: 'budget_hourly_rate',
          hide_when_grouped_column_collapsed: true,
          editable_predicate: (params) => blank(data_get(params.node, 'data.actual_labours')),
          rendering_config: {
            placeholder: (params) => when(filled(data_get(params.node, 'data.actual_labours')), {
              then: () => 'Please click the task name to manage costing for this task',
            }),
          },
          serializer: (value, params) => {
            value = to_numeric(value);

            return ({
              success: true,
              updates: {
                budget_hourly_rate: value,
                estimated_labour_cost: multiply({
                  multiplicant: value,
                  multiplier: data_get(params.node, 'data.estimated_duration'),
                }),
              },
            });
          },
        },
        {
          label: 'Est. Labor Cost',
          field_type: 'currency',
          field_name: 'estimated_labour_cost',
          editable: false,
        },
        {
          label: 'Est. Material Cost',
          field_type: 'currency',
          field_name: 'estimated_material_cost',
          editable_predicate: (params) => blank(data_get(params.node, 'data.actual_materials')),
          rendering_config: {
            placeholder: (params) => when(filled(data_get(params.node, 'data.actual_materials')), {
              then: () => 'Please click the task name to manage costing for this task',
            }),
          },
        },
        {
          label: 'Est. Equipment Cost',
          field_type: 'currency',
          field_name: 'estimated_equipment_cost',
          editable_predicate: (params) => blank(data_get(params.node, 'data.actual_equipments')),
          rendering_config: {
            placeholder: (params) => when(filled(data_get(params.node, 'data.estimated_equipments')), {
              then: () => 'Please click the task name to manage costing for this task',
            }),
          },
        },
        {
          label: 'Est. Other Cost',
          field_type: 'currency',
          field_name: 'estimated_other_cost',
          editable_predicate: (params) => blank(data_get(params.node, 'data.actual_other_items')),
          rendering_config: {
            placeholder: (params) => when(filled(data_get(params.node, 'data.actual_other_items')), {
              then: () => 'Please click the task name to manage costing for this task',
            }),
          },
        },
        {
          label: 'Est. Total Cost',
          field_type: 'currency',
          editable: false,
          deserializer: (params) => {
            if (params.node.group) {
              return;
            }

            const data: DisplayedJobTask = params.data;

            const total = sum([
              data.estimated_labour_cost,
              data.estimated_material_cost,
              data.estimated_equipment_cost,
              data.estimated_other_cost,
            ]);

            return total;
          },
        },
        {
          label: 'Est. Revenue',
          field_type: 'currency',
          field_name: 'estimated_revenue',
        },
      ]
    },
    {
      label: 'Actual',
      children: [
        {
          label: 'WO Number',
          field_name: 'wo_number',
        },
        {
          label: 'Actual Hours',
          field_type: 'numeric',
          field_name: 'actual_duration',
          editable: false,
          hide_when_grouped_column_collapsed: true,
        },
        {
          label: 'Actual Hourly Rate',
          field_type: 'currency',
          field_name: 'actual_hourly_rate',
          editable: false,
          hide_when_grouped_column_collapsed: true,
        },
        {
          label: 'Actual Labor Cost',
          field_type: 'currency',
          field_name: 'actual_labour_cost',
          editable: false,
        },
        {
          label: 'Actual Material Cost',
          field_type: 'currency',
          field_name: 'actual_material_cost',
          editable: false,
        },
        {
          label: 'Actual Equipment Cost',
          field_type: 'currency',
          field_name: 'actual_equipment_cost',
          editable: false,
        },
        {
          label: 'Actual Other Cost',
          field_type: 'currency',
          field_name: 'actual_other_cost',
          editable: false,
        },
        {
          label: 'Actual Total Cost',
          field_type: 'currency',
          editable: false,
          deserializer: (params) => {
            if (params.node.group) {
              return;
            }

            const data: DisplayedJobTask = params.data;

            const total = sum([
              data.actual_labour_cost,
              data.actual_material_cost,
              data.actual_equipment_cost,
              data.actual_other_cost,
            ]);

            return total;
          },
        },
        {
          label: 'Actual Revenue',
          field_type: 'currency',
          field_name: 'actual_revenue',
          editable: false,
        },
      ]
    },
    {
      label: 'Variance %',
      children: [
        {
          label: 'Cost',
          editable: false,
          field_type: 'percentage',
          deserializer: (params) => {
            const data: DisplayedJobTask = params.node.data;

            const estimate = sum([
              data.estimated_labour_cost,
              data.estimated_equipment_cost,
              data.estimated_material_cost,
              data.estimated_other_cost,
            ]);

            const actual = sum([
              data.actual_labour_cost,
              data.actual_equipment_cost,
              data.actual_material_cost,
              data.actual_other_cost,
            ]);

            return variance_percentage({
              estimate,
              actual,
              negate: true,
            });
          },
        },
        {
          label: 'Revenue',
          editable: false,
          field_type: 'percentage',
          deserializer: (params) => {
            const data: DisplayedJobTask = params.node.data;

            return variance_percentage({
              estimate: data.estimated_revenue,
              actual: data.actual_revenue,
            });
          },
        },
      ]
    },
  ];

  readonly menus: FieldmagicDataGridContextMenu[] = [
    {
      label: 'Add Milestone',
      action: (() => this.onAddMilestone()),
      is_on_empty_node: true,
    },
    {
      label: 'Delete Milestone',
      action: ((params) => this._onRemoveMilestone(params)),
      is_for_group_action: true,
    },
    {
      label: 'Add task',
      action: ((params) => this.onAddTask({
        params,
      })),
      is_for_group_action: true,
      is_on_empty_node: true,
    },
    {
      label: 'Add task above',
      action: ((params) => this.onAddTask({
        params,
        above: true,
      })),
      is_row_action: true,
    },
    {
      label: 'Add task below',
      action: ((params) => this.onAddTask({
        params,
        below: true,
      })),
      is_row_action: true,
    },
    {
      label: 'Delete Task',
      action: ((params) => this._onDeleteTask(params)),
      is_row_action: true,
    },
    {
      label: 'Schedule',
      action: (params) => this._onSchedule(params),
      is_row_action: true,
      predicate: (params) => data_is_true(data_get(params.node, 'data.__persisted')),
    },
  ];

  private _grid?: FieldmagicDataGridApi<DisplayedJobTask>;

  private _onFetchingSubscription?: Subscription;
  private _onSchedulingSubscription?: Subscription;

  constructor(
    private readonly _api: ApiService,
    private readonly _dialog: DialogService,
    private readonly _records: RecordService,
    private readonly _notifications: NotificationService,
    private readonly _translator: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    this._initData();

    this.dataStateKey$.next(spf('jobs:cost_centers:%s', {
      args: [this.data$.value.id],
    }));
  }

  ngOnDestroy(): void {
    if (this._onFetchingSubscription) {
      this._onFetchingSubscription.unsubscribe();
    }

    if (this._onSchedulingSubscription) {
      return this._onSchedulingSubscription.unsubscribe();
    }
  }

  onGridReady = (event: FieldmagicDataGridOnReadyEvent<DisplayedJobTask>) => (this._grid = event.api);

  onAddMilestone(): void {
    const grid = this._grid;

    if (blank(grid)) {
      return;
    }

    const milestones = arr_filter(grid.groups, {
      predicate: (node) => node.field === 'milestone',
    });

    grid.addItems({
      milestone: {
        id: generate_id(),
        name: spf(this._translator.instant('Unnamed Milestone %d'), {
          args: [arr_count(milestones) + 1],
        }),
      },
      id: generate_id(),
      name: spf(this._translator.instant('Unnamed task %d'), {
        args: [1],
      }),
    });
  }

  onAddTask(opts: {
    params?: FieldmagicDataGridContextMenuParams;
    above?: boolean;
    below?: boolean;
  } = {}): void {
    opts = Object.assign(
      {
        above: false,
        below: false,
      },
      opts,
    );

    if (blank(this._grid)) {
      return;
    }

    let milestone: OptionalValue<DisplayedJobTask['milestone']>;

    if (filled(opts.params)
        && filled(opts.params.node)
        && opts.params.node.group
      ) {
      const siblings = opts.params.node.allLeafChildren;

      if (blank(siblings)) {
        return;
      }

      milestone = data_get(arr_first(siblings).data, 'milestone');
    } else if (filled(opts.params) && filled(opts.params.node)) {
      milestone = data_get(opts.params.node.data, 'milestone');
    }

    let suffix: number = 0;

    if (filled(opts.params) && filled(opts.params.node)) {
      suffix = when(opts.params.node.group, {
        then: () => arr_count(opts.params.node.allLeafChildren),
        else: () => arr_count(whenFilled(opts.params.node.parent, {
          then: () => opts.params.node.parent.allLeafChildren,
        })),
      });
    } else {
      suffix = arr_count(arr_filter(this._grid.data, {
        predicate: (data) => blank(data.milestone),
      }));
    }

    this._grid.addItems({
      id: generate_id(),
      name: spf(this._translator.instant('Unnamed task %d'), {
        args: [suffix + 1]
      }),
      milestone,
    }, {
      after: when(opts.below, {
        then: () => opts.params.node,
      }),
      before: when(opts.above, {
        then: () => opts.params.node,
      }),
    });
  }

  onSave(): void {
    if (blank(this._grid)) {
      return;
    }

    const tasks: DisplayedJobTask[] = [];

    for (const data of this._grid.savable()) {
      tasks.push({
        ...data,
        record_order: data.__index,
      });
    }

    observe({
      before: () => this.saving$.next(true),
      after: () => this.saving$.next(false),
      observable: () =>
        defer(() => {
          const data = {
            job_tasks: tasks,
          };

          return this._records.saveRecord('jobs', data, this.data$.value.id);
        }),
    }).subscribe(() => {
      this._notifications.notifySuccess('Tasks saved successfully.'),
      this._initData();
    });

    return;
  }

  onAssignTasks(): void {
    if (blank(this._grid)) {
      return;
    }

    const tasks: DisplayedJobTask[] = [];

    for (const data of this._grid.savable()) {
      tasks.push({
        ...data,
      });
    }

    this._dialog.show({
      component: JobsAssignProjectTaskDialogComponent,
      data: <JobsAssignProjectTaskDialogData>{
        tasks,
        job_id: this.data$.value.id,
      },
      size: 'lg',
      afterClosing: (result: JobsAssignProjectTaskDialogResult) => {
        if (blank(result)) {
          return;
        }

        if (blank(this._grid)) {
          return;
        }

        this._grid.updateItems(result.tasks);
      },
    });
  }

  onApplyTemplate = () => this._dialog.show({
    component: ProjectTasksApplyTemplateDialogComponent,
    data: <ProjectTasksApplyTemplateDialogData>{
      'job_id': this.data$.value.id,
    },
    afterClosing: (result: ProjectTasksApplyTemplateDialogResult) => {
      if (blank(result)) {
        return;
      }

      this.tasks$.next(result.tasks);
    },
  });

  onManageBreakdown(event: FieldmagicDataGridOnRenderedDataClickedEvent): void {
    if (event.node.group
      || blank(event.node.data)
    ) {
      return;
    }

    const costings: DisplayedTaskCost[] = [
      ... arr_reduce((event.node.data as DisplayedJobTask).actual_labours, {
        initial_value: [],
        reducer: (initial, labour) => {
          initial.push(<DisplayedTaskCost>{
            __category: 'labour',
            id: labour.id,
            item: labour.item,
            estimated_quantity: labour.estimated_duration,
            estimated_cost: labour.budget_hourly_rate,
            estimated_unit_price: labour.estimated_unit_price,
            estimated_revenue: labour.estimated_revenue,
            estimated_markup: labour.estimated_markup,
            actual_cost: labour.actual_hourly_rate,
            actual_quantity: labour.actual_duration,
            actual_revenue: labour.actual_revenue,
            __persisted: labour.__persisted,
          });

          return initial;
        },
      }),
      ... arr_reduce((event.node.data as DisplayedJobTask).actual_materials, {
        initial_value: [],
        reducer: (initial, material) => {
          initial.push(<DisplayedTaskCost>{
            __category: 'material',
            id: material.id,
            item: fallback(material.item, {
              to: () => material.description,
            }),
            estimated_quantity: material.estimated_quantity,
            estimated_cost: material.estimated_unit_cost,
            estimated_unit_price: material.estimated_unit_price,
            estimated_revenue: material.estimated_revenue,
            estimated_markup: material.estimated_markup,
            actual_cost: material.actual_unit_cost,
            actual_quantity: material.actual_quantity,
            actual_revenue: material.actual_revenue,
            __selected: true,
            __persisted: material.__persisted,
          });

          return initial;
        },
      }),
      ... arr_reduce((event.node.data as DisplayedJobTask).actual_equipments, {
        initial_value: [],
        reducer: (initial, equipment) => {
          initial.push(<DisplayedTaskCost>{
            __category: 'equipment',
            id: equipment.id,
            item: equipment.item,
            estimated_quantity: equipment.estimated_quantity,
            estimated_cost: equipment.estimated_unit_cost,
            estimated_unit_price: equipment.estimated_unit_price,
            estimated_revenue: equipment.estimated_revenue,
            estimated_markup: equipment.estimated_markup,
            actual_cost: equipment.actual_unit_cost,
            actual_quantity: equipment.actual_quantity,
            actual_revenue: equipment.actual_revenue,
            __persisted: equipment.__persisted,
          });

          return initial;
        },
      }),
      ... arr_reduce((event.node.data as DisplayedJobTask).actual_other_items, {
        initial_value: [],
        reducer: (initial, other) => {
          initial.push(<DisplayedTaskCost>{
            __category: 'other',
            id: other.id,
            item: other.description,
            estimated_quantity: other.estimated_quantity,
            estimated_cost: other.estimated_unit_cost,
            estimated_unit_price: other.estimated_unit_price,
            estimated_revenue: other.estimated_revenue,
            estimated_markup: other.estimated_markup,
            actual_cost: other.actual_unit_cost,
            actual_quantity: other.actual_quantity,
            actual_revenue: other.actual_revenue,
            __persisted: other.__persisted,
          });

          return initial;
        },
      }),
    ];

    this._dialog.show({
      component: JobsManageTaskCostBreakdownDialogComponent,
      data: <JobsManageTaskCostBreakdownDialogData>{
        task: event.node.data as DisplayedJobTask,
        costings: costings,
        job_id: this.data$.value.id,
      },
      size: 'xl',
      afterClosing: (result: JobsManageTaskCostBreakdownDialogResult) => {
        if (blank(result)) {
          return;
        }

        const task: DisplayedJobTask = {
          ... event.node.data,
          estimated_duration: sum(arr_map(result.labours, {
            transformer: (value) => value.estimated_duration,
          })),
          budget_hourly_rate: sum(arr_map(result.labours, {
            transformer: (value) => value.budget_hourly_rate,
          })),
          estimated_labour_cost: sum(arr_map(result.labours, {
            transformer: (value) => multiply({
              multiplier: value.estimated_duration,
              multiplicant: value.budget_hourly_rate,
            }),
          })),
          estimated_material_cost: sum(arr_map(result.materials, {
            transformer: (value) => multiply({
              multiplier: value.estimated_quantity,
              multiplicant: value.estimated_unit_cost,
            }),
          })),
          estimated_equipment_cost: sum(arr_map(result.equipments, {
            transformer: (value) => multiply({
              multiplier: value.estimated_quantity,
              multiplicant: value.estimated_unit_cost,
            }),
          })),
          estimated_other_cost: sum(arr_map(result.others, {
            transformer: (value) => multiply({
              multiplier: value.estimated_quantity,
              multiplicant: value.estimated_unit_cost,
            }),
          })),
          estimated_revenue: sum([
            ... arr_map(result.materials, {
              transformer: (value) => value.estimated_revenue,
            }),
            ... arr_map(result.labours, {
              transformer: (value) => value.estimated_revenue,
            }),
            ... arr_map(result.equipments, {
              transformer: (value) => value.estimated_revenue,
            }),
            ... arr_map(result.others, {
              transformer: (value) => value.estimated_revenue,
            }),
          ]),
          actual_materials: result.materials,
          actual_labours: result.labours,
          actual_equipments: result.equipments,
          actual_other_items: result.others,
        };

        this._grid.updateItems(task);
      },
    });
  }

  onRefresh = () => this._initData();

  private _onRemoveMilestone(params: FieldmagicDataGridContextMenuParams): void {
    if (!params.node.group || blank(this._grid)) {
      return;
    }

    const siblings = params.node.allLeafChildren;

    if (blank(siblings)) {
      return;
    }

    this._grid.removeItems(arr_map(siblings, {
      transformer: (node) => node.data,
    })).subscribe();
  }

  private _onDeleteTask(params: FieldmagicDataGridContextMenuParams): void {
    if (params.node.group || blank(this._grid)) {
      return;
    }

    this._grid.removeItems(params.node.data).subscribe();
  }

  private _onSchedule(params: FieldmagicDataGridContextMenuParams): void {
    if (blank(params.node.data)
      || params.node.group
    ) {
      return;
    }

    const data: DisplayedJobTask = params.node.data;
    const job = <JobMetadata>this.data$.value;

    this._onSchedulingSubscription = observe({
      before: () => this.scheduling$.next(true),
      after: () => this.scheduling$.next(false),
      observable: () => this._api.callPrivate$({
        path: '/jobs/work_locations',
        body: {
          job_id: job.id,
          atleast_one: true,
        },
        expectsNewFormat: true,
        onSuccessResponse: (response) => response.get('items')
      }).pipe(
        map((locations) => filled(locations)),
        switchMap((enabledWorkLocations) => this._dialog.show$({
          component: FieldmagicSchedulerDialogComponent,
          data: <FieldmagicSchedulerDialogData>{
            timeline_view: 'week',
            calendar_settings: {
              work_location: {
                enabled: enabledWorkLocations,
                job_id: job.id,
              }
            },
            additional_label: data.name,
            schedulable: new ProjectTaskCalendarEventMetadata({
              ... data,
              job: job,
            }),
          },
          size: 'full',
        })),
      ),
    }).subscribe();
  }

  private _initData(): void {
    this._onFetchingSubscription = observe({
      before: () => this.fetching$.next(true),
      after: () => this.fetching$.next(false),
      observable: () =>
        this._api.callPrivate$({
          path: '/jobs/tasks',
          body: {
            job_id: this.data$.value.id,
          },
          expectsNewFormat: true,
          onSuccessResponse: (response) => response.get('items'),
        }),
    }).subscribe((tasks: DisplayedJobTask[]) => this.tasks$.next(tasks));
  }
}

@Component({
  selector: `jobs-assign-project-tasks-dialog`,
  template: `
    <fieldmagic-dialog
      label="Assign Tasks"
      icon="users"
      [instance]="this"
      [dataStateKey]="dataStateKey$ | async"
    >
      <ng-template fieldmagicDialogButtonsTmp>
        <fieldmagic-primary-button
          label="Assign"
          icon="clipboard-list-check"
          (click)="onAssign()"
        >
        </fieldmagic-primary-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <fieldmagic-data-grid
          size="lg"
          [options]="options"
          [columns]="columns"
          [data]="data$ | async"
          (onReady)="onGridReady($event)"
          [dataStateKey]="dataStateKey$ | async"
        >
        </fieldmagic-data-grid>
      </ng-template>
    </fieldmagic-dialog>
  `,
})
export class JobsAssignProjectTaskDialogComponent implements OnInit {
  readonly data$ = new BehaviorSubject<AssignableProjectRoleData[]>([]);

  readonly dataStateKey$ = new BehaviorSubject<OptionalValue<string>>(null);

  readonly options: FieldmagicDataGridOptions = {
    enable_row_selection: 'multi',
    enable_totals: false,
    enable_charts: false,
  };

  readonly columns: FieldmagicDataGridColumnDef[] = [
    {
      label: 'Role',
      field_name: 'role',
      field_type: 'dropdown',
      editable: false,
      grouped: true,
      auto_grouped: true,
      hidden: true,
      pinned: 'left',
      group_id: (value) => data_get(value, 'text'),
    },
    {
      label: 'Task',
      children: [
        {
          label: 'Task',
          field_name: 'name',
          editable: false,
          enable_checkbox: true,
        },
        {
          label: 'Description',
          field_name: 'description',
          field_type: 'textarea',
          editable: false,
          hide_when_grouped_column_collapsed: true,
        },
      ],
    },
    {
      label: 'Assignee',
      children: [
        {
          label: 'Assignee',
          field_name: 'user',
          field_type: 'relate',
          editable: true,
          input_config: <FieldmagicDataGridRelateDropdownInputConfig> {
            remote_config: {
              module_name: 'users',
            },
            placeholder: 'Please select a user',
            enable_mass_update: true,
          },
        },
        {
          label: 'Department',
          field_name: 'department',
          field_type: 'relate',
          editable: false,
          hide_when_grouped_column_collapsed: true,
        },
      ]
    },
    {
      label: 'Status',
      children: [
          {
            label: 'Ready to start?',
            field_name: 'is_ready',
            field_type: 'checkbox',
            serializer: (value: boolean, params) => ({
              success: true,
              updates: {
                'date_ready': when(value, {
                  then: () => to_utc_datetime('now', {
                    as_server_compatible: true,
                  }),
                }),
                'is_ready': value,
              },
            }),
          },
          {
            label: 'Progress',
            field_name: 'progress_percentage',
            field_type: 'progressive_percentage',
            enable_grouping: true,
            serializer: (value: number, params) => {
              value = to_numeric(value);

              const updates = {
                'progress_percentage': value,
                'date_completed': when(value >= 100, {
                  then: () => to_utc_datetime('now', {
                    as_server_compatible: true,
                  }),
                }),
              };

              if (value === 0) {
                updates['date_started'] = null;
              } else if (value > 0 && value < 100 && blank(data_get(params.node.data, 'date_started'))) {
                updates['date_started'] = to_utc_datetime('now', {
                  as_server_compatible: true,
                });
              }

              return {
                success: true,
                updates,
              };
            },
          },
          {
            label: 'Date Ready',
            field_name: 'date_ready',
            field_type: 'datetime',
            hide_when_grouped_column_collapsed: true,
          },
          {
            label: 'Date Started',
            field_name: 'date_started',
            field_type: 'datetime',
            hide_when_grouped_column_collapsed: true,
          },
          {
            label: 'Date Completed',
            field_name: 'date_completed',
            field_type: 'datetime',
            hide_when_grouped_column_collapsed: true,
          },
      ]
    },
    {
      label: 'Estimates',
      children: [
        {
          label: 'Est. Hours',
          field_type: 'numeric',
          field_name: 'estimated_duration',
          editable: false,
          hide_when_grouped_column_collapsed: true,
        },
        {
          label: 'Budget Hourly Rate',
          field_type: 'numeric',
          field_name: 'budget_hourly_rate',
          editable: false,
          hide_when_grouped_column_collapsed: true,
        },
        {
          label: 'Est. Labor Cost',
          field_type: 'currency',
          field_name: 'estimated_labour_cost',
          editable: false,
        },
        {
          label: 'Est. Material Cost',
          field_type: 'currency',
          editable: false,
          field_name: 'estimated_material_cost',
        },
        {
          label: 'Est. Equipment Cost',
          field_type: 'currency',
          editable: false,
          field_name: 'estimated_equipment_cost',
        },
        {
          label: 'Est. Other Cost',
          field_type: 'currency',
          editable: false,
          field_name: 'estimated_other_cost',
        },
        {
          label: 'Est. Total Cost',
          field_type: 'currency',
          editable: false,
          deserializer: (params) => {
            if (params.node.group) {
              return;
            }

            const data: DisplayedJobTask = params.data;
            const labor = multiply({
              multiplicant: data.budget_hourly_rate,
              multiplier: data.estimated_duration,
            });

            const total = sum([
              labor,
              data.estimated_material_cost,
              data.estimated_equipment_cost,
              data.estimated_other_cost,
            ]);

            return total;
          },
        },
        {
          label: 'Est. Revenue',
          field_type: 'currency',
          field_name: 'estimated_revenue',
        },
      ]
    },
  ];

  private _grid?: FieldmagicDataGridApi<AssignableProjectRoleData>;

  constructor(
    @Inject(DIALOG_DATA) private readonly _data: JobsAssignProjectTaskDialogData,
    private readonly _dialog: DialogService,
    private readonly _translator: TranslateService,
  ) {}

  ngOnInit(): void {
    this.dataStateKey$.next(spf('jobs:cost_centers:assignable_tasks:%s', {
      args: [this._data.job_id],
    }));

    const roles: Record<string, AssignableProjectRoleData> = {};

    for (const task of this._data.tasks) {
      let role: DisplayedJobTask['role'] = task.role;

      if (blank(role)) {
        role = {
          id: 'no_role',
          text: this._translator.instant('No Role'),
        };
      }

      const assignable = fallback(data_get(roles, role.id), {
        to: () => ({
          id: role.id,
          name: role.text,
          tasks: <DisplayedJobTask[]>[],
        })
      });

      const tasks = [
        ... assignable.tasks,
        task,
      ];

      roles[assignable.id] = {
        ... assignable,
        tasks,
      };
    }

    /// sort object and prioritize valid roles
    /// Thanks AI!
    const sorted = Object.entries(roles)
      .sort(([a], [b]) => {
        if (a === 'no_role') {
          return 1;
        }

        if (b === 'no_role') {
          return -1;
        }

        return a.localeCompare(b);
      })
      .map(([, value]) => ({
        ... value,
      }));

    this.data$.next(arr_reduce(sorted, {
      initial_value: <AssignableProjectRoleData[]>[],
      reducer: (initial, role) => {
        for (const task of role.tasks) {
          initial.push({
            ... task,
            role: {
              id: role.id,
              text: role.name,
            },
            __selected: role.id !== 'no_role',
          })
        }

        return initial;
      },
    }));
  }

  onGridReady = (event: FieldmagicDataGridOnReadyEvent<DisplayedJobTask>) => (this._grid = event.api);

  onAssign(): void {
    const grid = this._grid;

    if (blank(grid)) {
      return;
    }

    const tasks: DisplayedJobTask[] = [];

    for (const data of grid.savable()) {
      if (data_get(data, 'role.id') === 'no_role') {
        delete data['role'];
      }

      tasks.push(data);
    }

    this._dialog.close({
      instance: this,
      result: <JobsAssignProjectTaskDialogResult>{
        tasks,
      },
    });
  }
}

type DisplayedJobTask = Identifiable & FieldmagicDataGridPersistedData & {
  name: string;
  milestone?: {
    id: string;
    name: string;
    record_order?: number;
  };

  progress_percentage?: number;

  estimated_duration?: number;
  budget_hourly_rate?: number;
  estimated_labour_cost?: number;
  estimated_material_cost?: number;
  estimated_equipment_cost?: number;
  estimated_other_cost?: number;
  estimated_revenue?: number;
  estimated_markup?: number;
  description?: string;

  wo_number?: string;
  actual_duration?: number;
  actual_hourly_rate?: number;
  actual_labour_cost?: number;
  actual_material_cost?: number;
  actual_equipment_cost?: number;
  actual_other_cost?: number;
  actual_revenue?: number;
  record_order?: number;

  user?: FieldmagicRelateOption;
  role?: FieldmagicDropdownOption<Identifiable>;
  department?: FieldmagicRelateOption;

  is_ready?: boolean;

  actual_labours?: ActualTaskLabour[];
  actual_materials?: ActualTaskMaterial[];
  actual_equipments?: ActualTaskEquipment[];
  actual_other_items?: ActualTaskOtherItem[];
};


type JobsAssignProjectTaskDialogData = {
  tasks: DisplayedJobTask[];
  job_id: string;
};

type JobsAssignProjectTaskDialogResult = {
  tasks?: DisplayedJobTask[];
};

type AssignableProjectRoleData = DisplayedJobTask & FieldmagicDataGridSelectableData;

@Component({
  selector: 'project-tasks-apply-template-dialog',
  template: `
    <fieldmagic-dialog
      label="Apply Project Template"
      icon="clipboard"
      [instance]="this"
    >
      <ng-template fieldmagicDialogButtonsTmp>
        <fieldmagic-primary-button
          label="Apply"
          icon="save"
          (click)="onApply()"
          [isInProgress]="replacing$ | async"
        >
        </fieldmagic-primary-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <fieldmagic-relate-input
          moduleName="project_templates"
          [(ngModel)]="template"
          label="Project Template"
          [withRequiredMarker]="true"
          [errors]="errors.template"
        >
        </fieldmagic-relate-input>
      </ng-template>
    </fieldmagic-dialog>
  `,
})
export class ProjectTasksApplyTemplateDialogComponent {
  template?: OptionalValue<FieldmagicRelateInputValue>;

  errors: FieldmagicInputErrors = {};

  readonly replacing$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(DIALOG_DATA) private readonly _data: ProjectTasksApplyTemplateDialogData,
    private readonly _dialog: DialogService,
    private readonly _api: ApiService,
    private readonly _notifications: NotificationService,
  ) {}

  onApply(): void {
    if (! this._isValid()) {
      return;
    }

    observe({
      before: () => this.replacing$.next(true),
      after: () => this.replacing$.next(false),
      observable: () => this._notifications.sendConfirmation('Are you sure you want to proceed? This will permanently replace all tasks in this job.')
        .pipe(
          filter((result) => result.answer),
          switchMap(() => this._api.callPrivate$({
            path: '/jobs/apply_project_template',
            body: {
              'job_id': this._data.job_id,
              'project_template_id': this.template.id,
            },
            expectsNewFormat: true,
            notifyOnFailure: true,
            onSuccessResponse: (response) => response.get('items'),
          }))
        )
    }).pipe(
      tap(() => this._notifications.notifySuccess('Template applied successfully.')),
    ).subscribe((tasks) => this._dialog.close({
      instance: this,
      result: <ProjectTasksApplyTemplateDialogResult>{
        tasks,
      },
    }));

    return;
  }
  private _isValid(): boolean {
    this.errors = validate_input_data({
      data: {
        template: this.template,
      },
      rules: {
        template: ['required'],
      },
    });

    return blank(this.errors);
  }
}

type ProjectTasksApplyTemplateDialogData = {
  job_id: string;
};

type ProjectTasksApplyTemplateDialogResult = {
  tasks: DisplayedJobTask[];
};

@Component({
  selector: 'jobs-manage-task-costs-breakdown-dialog',
  template: `
    <fieldmagic-dialog
      [label]="'Task Costs Breakdown: [%s]' | translate | sprintf: [task$ | async | data_get: 'name']"
      icon="dollar-sign"
      [instance]="this"
      [dataStateKey]="dataStateKey$ | async"
    >
      <ng-template fieldmagicDialogButtonsTmp>
        <fieldmagic-primary-button
          label="Create Materials"
          icon="box-full"
          [isInProgress]="creating$ | async"
          (click)="onCreateMaterials()"
        >
        </fieldmagic-primary-button>
        <fieldmagic-primary-button
          purpose="save"
          [disabled]="creating$ | async"
          (click)="onSave()"
        >
        </fieldmagic-primary-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <fieldmagic-data-grid
          size="md"
          [options]="options"
          [data]="data$ | async"
          [columns]="columns"
          [menus]="menus"
          [dataStateKey]="dataStateKey$ | async"
          (onReady)="onGridReady($event)"
        >
        </fieldmagic-data-grid>
      </ng-template>
    </fieldmagic-dialog>
  `
})
export class JobsManageTaskCostBreakdownDialogComponent implements OnInit {
  readonly task$ = new BehaviorSubject<OptionalValue<DisplayedJobTask>>(null);
  readonly data$ = new BehaviorSubject<DisplayedTaskCost[]>([]);
  readonly dataStateKey$ = new BehaviorSubject<OptionalValue<string>>(null);

  readonly creating$ = new BehaviorSubject<boolean>(false);

  readonly options: FieldmagicDataGridOptions = {
    enable_row_selection: 'multi',
  };

  readonly columns: FieldmagicDataGridColumnDef[] = [
    {
      label: 'Category',
      editable: false,
      auto_grouped: true,
      grouped: true,
      pinned: 'left',
      hidden: true,
      deserializer: (params) => this._translator.instant(data_get(params.data, '__category')),
    },
    {
      label: 'Product/Service',
      field_type: 'relate',
      field_name: 'item',
      is_group_child_count_ref: true,
      enable_checkbox: (data) => data_get(data, '__category') === 'material',
      input_config: (params) => {
        const data: DisplayedTaskCost = data_get(params.node, 'data');

        return <FieldmagicDataGridRelateDropdownInputConfig>{
          placeholder: 'Please select an item from the catalogue..',
          remote_config: {
            ... (data_get(data, '__category') !== 'equipment' && {
              module_name: 'items',
              filter: (data: DisplayedTaskCost) => ({
                labor: data.__category === 'labour',
                active: true,
              }),
            }),
            ... (data_get(data, '__category') === 'equipment' && {
              module_name: 'resources',
            }),
          },
          use_text_when: () => data_get(params.data, '__category') === 'other'
            || (data_get(params.data, '__category') === 'material' && is_string(data_get(params.data, 'item'))),
          text_config: {
            placeholder: 'Tell me something about the product/service',
          },
        };
      },
      serializer: (value, params) => {
        let cost: number = 0;

        if (data_get(params.node, 'data.__category') === 'labour'
          || data_get(params.node, 'data.__category') === 'equipment'
        ) {
          cost = to_numeric(data_get(value, 'hourly_cost'));
        } else if (data_get(params.node, 'data.__category') === 'material'
          || data_get(params.node, 'data.__category') === 'other'
        ) {
          cost = to_numeric(data_get(value, 'unit_cost'));
        }

        let price: number = 0;

        if (data_get(params.node, 'data.__category') === 'labour'
          || data_get(params.node, 'data.__category') === 'material'
          || data_get(params.node, 'data.__category') === 'other'
        ) {
          price = to_numeric(data_get(value, 'unit_price'));
        } else if (data_get(params.node, 'data.__category') === 'equipment') {
          /// backward compatiblity already advice author of resources
          /// to use unit_price as column name for consistency
          price = to_numeric(fallback(data_get(value, 'unit_price'), {
            to: () => data_get(value, 'sell_price')
          }));
        }

        return {
          success: true,
          updates: {
            item: value,
            estimated_cost: cost,
            estimated_revenue: multiply({
              multiplicant: price,
              multiplier: data_get(params.node, 'data.estimated_quantity'),
            }),
            estimated_unit_price: price,
            estimated_markup: ceil_percentage(markup({
              unit_price: price,
              unit_cost: cost,
            })),
          },
        };
      },
    },
    {
      label: 'Estimates',
      children: [
         {
          label: 'Est. Hours/Quantity',
          field_type: 'numeric',
          field_name: 'estimated_quantity',
          aggregation: 'sum',
          serializer: (value, params) => ({
            success: true,
            updates: <PartialData<DisplayedTaskCost>>{
              estimated_quantity: value,
              estimated_revenue: multiply({
                multiplicant: data_get(params.node, 'data.estimated_unit_price'),
                multiplier: value,
              }),
              estimated_markup: ceil_percentage(markup({
                unit_price: data_get(params.node, 'data.estimated_unit_price'),
                unit_cost: data_get(params.node, 'data.estimated_cost'),
              })),
            },
          }),
        },
        {
          label: 'Est. Hourly Rate/Cost',
          field_type: 'currency',
          field_name: 'estimated_cost',
          aggregation: 'sum',
          serializer: (value, params) => {
            value = to_numeric(value);

            const price = price_from_markup(value, data_get(params.node, 'data.estimated_markup'));

            return ({
              success: true,
              updates: {
                estimated_cost: value,
                estimated_unit_price: price,
                estimated_revenue: multiply({
                  multiplicant: price,
                  multiplier: data_get(params.node, 'data.estimated_quantity'),
                }),
              },
            });
          },
        },
        {
          label: 'Est. Sell Price',
          field_type: 'currency',
          field_name: 'estimated_unit_price',
          aggregation: 'sum',
          hidden: true,
        },
        {
          label: 'Est. Markup %',
          field_type: 'percentage',
          field_name: 'estimated_markup',
          aggregation: 'sum',
          serializer: (value, params) => {
            value = ceil_percentage(value);

            const price = price_from_markup(data_get(params.node, 'data.estimated_cost'), value);

            return {
              success: true,
              updates: {
                estimated_markup: value,
                estimated_unit_price: price,
                estimated_revenue: multiply({
                  multiplicant: price,
                  multiplier: data_get(params.node, 'data.estimated_quantity'),
                }),
              },
            };
          },
        },
        {
          label: 'Total Cost',
          field_type: 'currency',
          editable: false,
          deserializer: (params) => {
            const data: DisplayedTaskCost = params.data;

            const total = multiply({
              multiplicant: data.estimated_cost,
              multiplier: data.estimated_quantity,
            });

            return total;
          },
        },
        {
          label: 'Est. Revenue',
          field_type: 'currency',
          field_name: 'estimated_revenue',
          aggregation: 'sum',
          serializer: (value, params) => {
            value = to_numeric(value);

            const price = divide({
              dividend: value,
              divisor: data_get(params.node, 'data.estimated_quantity'),
            });

            return {
              success: true,
              updates: {
                estimated_revenue: value,
                estimated_unit_price: price,
                estimated_cost: cost_from_markup(price, data_get(params.node, 'data.estimated_markup')),
              },
            };
          },
        },
      ]
    },
  ];

  readonly menus: FieldmagicDataGridContextMenu[] = [
    {
      label: 'Add Labour',
      is_for_group_action: true,
      is_row_action: true,
      predicate: (params) => filled(params.node) && (data_get(params.node.data, '__category') === 'labour'
        || (params.node.group && str_lower(params.value) === 'labour')),
      action: (params) => this._onNodeAdd('labour', params),
    },
    {
      label: 'Delete Labour',
      is_row_action: true,
      predicate: (params) => filled(params.node)
        && data_get(params.node.data, '__category') === 'labour',
      action: (params) => this._onNodeDelete('labour', params),
    },
    {
      label: 'Add Material',
      is_for_group_action: true,
      is_row_action: true,
      predicate: (params) => filled(params.node) && (data_get(params.node.data, '__category') === 'material'
        || (params.node.group && str_lower(params.value) === 'material')),
      action: (params) => this._onNodeAdd('material', params),
    },
    {
      label: 'Delete Material',
      is_row_action: true,
      predicate: (params) => filled(params.node)
        && data_get(params.node.data, '__category') === 'material',
      action: (params) => this._onNodeDelete('material', params),
    },
    {
      label: 'Add Equipment',
      is_for_group_action: true,
      is_row_action: true,
      predicate: (params) => filled(params.node) && (data_get(params.node.data, '__category') === 'equipment'
        || (params.node.group && str_lower(params.value) === 'equipment')),
      action: (params) => this._onNodeAdd('equipment', params),
    },
    {
      label: 'Delete Equipment',
      is_row_action: true,
      predicate: (params) => filled(params.node)
        && data_get(params.node.data, '__category') === 'equipment',
      action: (params) => this._onNodeDelete('equipment', params),
    },
    {
      label: 'Add Other Item',
      is_for_group_action: true,
      is_row_action: true,
      predicate: (params) => filled(params.node) && (data_get(params.node.data, '__category') === 'other'
        || (params.node.group && str_lower(params.value) === 'other')),
      action: (params) => this._onNodeAdd('other', params),
    },
    {
      label: 'Delete Other Item',
      is_row_action: true,
      predicate: (params) => filled(params.node)
        && data_get(params.node.data, '__category') === 'other',
      action: (params) => this._onNodeDelete('other', params),
    },
  ];

  private _grid: FieldmagicDataGridApi<DisplayedTaskCost>;

  constructor(
    @Inject(DIALOG_DATA) private readonly _data: JobsManageTaskCostBreakdownDialogData,
    private readonly _translator: TranslateService,
    private readonly _dialog: DialogService,
    private readonly _notifications: NotificationService,
    private readonly _api: ApiService,
  ) {}

  ngOnInit(): void {
    this.task$.next(this._data.task);

    this.dataStateKey$.next(spf('task_breakdown:%s', {
      args: [this._data.task.id],
    }));

    /// THANKS AI!
    const costings: DisplayedTaskCost[] = fallback(this._data.costings, {
      to: () => [],
    });

    if (blank(costings)
      || blank(arr_filter(costings, {
        predicate: (value) => value.__category === 'labour'
      }))
    ) {
      costings.push({
        id: generate_id(),
        __category: 'labour' as DisplayedTaskCost['__category'],
      });
    }

    if (blank(costings)
      || blank(arr_filter(costings, {
        predicate: (value) => value.__category === 'material'
      }))
    ) {
      costings.push({
        id: generate_id(),
        __category: 'material' as DisplayedTaskCost['__category'],
      });
    }

    if (blank(costings)
      || blank(arr_filter(costings, {
        predicate: (value) => value.__category === 'equipment'
      }))
    ) {
      costings.push({
        id: generate_id(),
        __category: 'equipment' as DisplayedTaskCost['__category'],
      });
    }

    if (blank(costings)
      || blank(arr_filter(costings, {
        predicate: (value) => value.__category === 'other'
      }))
    ) {
      costings.push({
        id: generate_id(),
        __category: 'other' as DisplayedTaskCost['__category'],
      });
    }

    this.data$.next(sort_costings(costings));
  }

  onGridReady = (event: FieldmagicDataGridOnReadyEvent<DisplayedTaskCost>) => this._grid = event.api;

  onSave(): void {
    if (blank(this._grid)) {
      return;
    }

    const labours: ActualTaskLabour[] = [];
    const materials: ActualTaskMaterial[] = [];
    const equipments: ActualTaskEquipment[] = [];
    const others: ActualTaskOtherItem[] = [];

    for (const datum of this._grid.savable()) {
      if (blank(datum.item)) {
        continue;
      }

      if (datum.__category === 'labour') {
        labours.push({
          id: datum.id,
          item: datum.item,
          estimated_duration: to_numeric(datum.estimated_quantity),
          budget_hourly_rate: to_numeric(datum.estimated_cost),
          estimated_unit_price: to_numeric(datum.estimated_unit_price),
          estimated_revenue: to_numeric(datum.estimated_revenue),
          estimated_markup: to_numeric(datum.estimated_markup),
          actual_duration: datum.actual_quantity,
          actual_revenue: to_numeric(datum.actual_revenue),
          __persisted: data_is_true(datum.__persisted),
        });
      } else if (datum.__category === 'material') {
        materials.push({
          id: datum.id,
          item: ! is_string(datum.item) ? datum.item : null,
          description: is_string(datum.item) ? datum.item : null,
          estimated_quantity: to_numeric(datum.estimated_quantity),
          estimated_unit_cost: to_numeric(datum.estimated_cost),
          estimated_unit_price: to_numeric(datum.estimated_unit_price),
          estimated_revenue: to_numeric(datum.estimated_revenue),
          estimated_markup: to_numeric(datum.estimated_markup),
          actual_quantity: datum.actual_quantity,
          actual_unit_cost: datum.actual_cost,
          actual_revenue: to_numeric(datum.actual_revenue),
          __persisted: data_is_true(datum.__persisted),
        });
      } else if (datum.__category === 'equipment') {
        equipments.push({
          id: datum.id,
          item: datum.item,
          estimated_quantity: to_numeric(datum.estimated_quantity),
          estimated_unit_cost: to_numeric(datum.estimated_cost),
          estimated_unit_price: to_numeric(datum.estimated_unit_price),
          estimated_revenue: to_numeric(datum.estimated_revenue),
          estimated_markup: to_numeric(datum.estimated_markup),
          actual_quantity: datum.actual_quantity,
          actual_unit_cost: datum.actual_cost,
          actual_revenue: to_numeric(datum.actual_revenue),
          __persisted: data_is_true(datum.__persisted),
        });
      } else if (datum.__category === 'other') {
        others.push({
          id: datum.id,
          description: datum.item as string,
          estimated_quantity: to_numeric(datum.estimated_quantity),
          estimated_unit_cost: to_numeric(datum.estimated_cost),
          estimated_unit_price: to_numeric(datum.estimated_unit_price),
          estimated_revenue: to_numeric(datum.estimated_revenue),
          estimated_markup: to_numeric(datum.estimated_markup),
          actual_quantity: datum.actual_quantity,
          actual_unit_cost: datum.actual_cost,
          actual_revenue: to_numeric(datum.actual_revenue),
          __persisted: data_is_true(datum.__persisted),
        });
      }
    }

    this._dialog.close({
      instance: this,
      result: {
        labours,
        materials,
        equipments,
        others,
      },
    });
  }

  onCreateMaterials(): void {
    if (blank(this._grid)
      || blank(this._data.job_id)
    ) {
      return;
    }

    const data: Record<string, any>[] = [];

    for (const datum of this._grid.selections) {
      if (datum.__category !== 'material') {
        continue;
      }

      if (blank(datum.item)) {
        continue;
      }

      const item = datum.item as FieldmagicRelateOption<ItemInterface>;

      data.push({
        type: 'product_catalog',
        quantity: datum.estimated_quantity,
        unit_cost: datum.estimated_cost,
        unit_price: datum.estimated_unit_price,
        markup: to_numeric(datum.estimated_markup),
        item_id: data_get(item, 'id'),
        item_name: data_get(item, 'text'),
        item_code: data_get(item, 'code'),
        job_id: this._data.job_id,
        activity_id: this.task$.value.id,
        sort_seq: 0,
        job_task_material_id: datum.id,
      });
    }

    if (blank(data)) {
      return this._notifications.notifyError('Please select atleast 1 material to create');
    }

    observe({
      before: () => this.creating$.next(true),
      after: () => this.creating$.next(false),
      observable: () => this._api.callPrivate$({
        path: '/record/save_multiple',
        body: {
          module: 'materials',
          data,
        },
        onSuccessResponse: (_) => true,
        notifyOnFailure: true,
      }).pipe(
        switchMap(() => this._api.callPrivate$({
          path: '/jobs/tasks',
          body: {
            job_id: this._data.job_id,
            task_id: this.task$.value.id,
          },
          onSuccessResponse: (response) => arr_first(response.get('items')),
        })),
      ),
    }).pipe(
      tap(() => this._notifications.notifySuccess('Materials created')),
    ).subscribe((item: DisplayedJobTask) => {
      this._dialog.close({
        instance: this,
        result: <JobsManageTaskCostBreakdownDialogResult>{
          labours: item.actual_labours,
          materials: item.actual_materials,
        },
      });
    });
  }

  private _onNodeAdd(category: DisplayedTaskCost['__category'], params: FieldmagicDataGridContextMenuParams): void {
    if (blank(this._grid)) {
      return;
    }

    this._grid.addItems({
      id: generate_id(),
      __category: category,
    });
  }

  private _onNodeDelete(category: DisplayedTaskCost['__category'], params: FieldmagicDataGridContextMenuParams): void {
    if (blank(this._grid)
      || blank(params.node.data)
      || data_get(params.node.data, '__category') !== category
    ) {
      return;
    }

    this._grid.removeItems(params.node.data).subscribe(() => {
      if (filled(params.node.parent)
        && blank(params.node.parent.allLeafChildren)
      ) {
        this._grid.addItems({
          id: generate_id(),
          __category: category,
        });
      }
    });
  }
}

type DisplayedTaskCost = Identifiable &
  DisplayedCategorizedTaskCost &
  FieldmagicDataGridSelectableData &
  FieldmagicDataGridPersistedData & {
  item?: FieldmagicRelateOption<ItemInterface> | string;
  estimated_cost?: number;
  estimated_quantity?: number;
  estimated_revenue?: number;
  estimated_unit_price?: number;
  estimated_markup?: number;
  actual_cost?: number;
  actual_quantity?: number;
  actual_revenue?: number;
};

type JobsManageTaskCostBreakdownDialogData = {
  task: DisplayedJobTask;
  job_id: string;
  costings?: DisplayedTaskCost[];
};

type JobsManageTaskCostBreakdownDialogResult = {
  labours?: ActualTaskLabour[];
  materials?: ActualTaskMaterial[];
  equipments?: ActualTaskEquipment[];
  others?: ActualTaskOtherItem[];
}

type ActualTaskLabour = Identifiable & FieldmagicDataGridPersistedData & {
  item: FieldmagicRelateOption;
  estimated_duration?: number;
  budget_hourly_rate?: number;
  estimated_unit_price?: number;
  estimated_revenue?: number;
  estimated_markup?: number;
  readonly actual_duration?: number;
  readonly actual_hourly_rate?: number;
  readonly actual_revenue?: number;
};

type ActualTaskMaterial = Identifiable & FieldmagicDataGridPersistedData & {
  item: FieldmagicRelateOption<{
    unit_price?: number;
    code?: string;
  }>;
  description?: string;
  estimated_quantity?: number;
  estimated_unit_cost?: number;
  estimated_unit_price?: number;
  estimated_revenue?: number;
  estimated_markup?: number;
  readonly actual_quantity?: number;
  readonly actual_unit_cost?: number;
  readonly actual_unit_price?: number;
  readonly actual_revenue?: number;
}

type ActualTaskEquipment = Identifiable & FieldmagicDataGridPersistedData & {
  item: FieldmagicRelateOption;
  estimated_quantity?: number;
  estimated_unit_cost?: number;
  estimated_unit_price?: number;
  estimated_revenue?: number;
  estimated_markup?: number;
  readonly actual_quantity?: number;
  readonly actual_unit_cost?: number;
  readonly actual_unit_price?: number;
  readonly actual_revenue?: number;
}

type ActualTaskOtherItem = Omit<ActualTaskMaterial, 'item'> & {
  description: string;
};